import { useCallback, useMemo, useState } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import { Alert, Button, Screen, Spinner, TabBar } from '../../components';
import { HomeIcon, PlusIcon, ReceiptIcon, SwapIcon, UsersIcon } from '../../components/icons';
import { useAuth } from '../../auth/useAuth';
import { useTripData } from '../../hooks/useTripData';
import type { TripContextValue } from './TripContext';
import type { ExpenseRow } from '../../data/types';
import { ExpenseSheet } from './ExpenseSheet';

/** Room for the fixed tab bar plus the floating add button above it. */
const BOTTOM_GUTTER = 'calc(96px + env(safe-area-inset-bottom))';

export function TripPage() {
  const { tripId = '' } = useParams();
  const { user } = useAuth();
  const { data, loading, error, reload } = useTripData(tripId);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRow | null>(null);

  const openExpenseSheet = useCallback((expense?: ExpenseRow) => {
    setEditing(expense ?? null);
    setSheetOpen(true);
  }, []);

  const context = useMemo<TripContextValue | null>(() => {
    if (!data) return null;

    const mine = data.members.find((m) => m.user_id === user?.id) ?? null;
    const names = new Map(data.members.map((m) => [m.id, m.display_name]));

    return {
      ...data,
      myMemberId: mine?.id ?? null,
      isOwner: mine?.role === 'owner',
      // Falls back rather than rendering "undefined": an expense can outlive
      // the seat that paid for it if someone was removed after settling up.
      nameOf: (memberId: string) => names.get(memberId) ?? 'Someone',
      reload,
      openExpenseSheet,
    };
  }, [data, user?.id, reload, openExpenseSheet]);

  if (loading && !data) {
    return (
      <Screen center maxWidth={560}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner size={28} label="Loading the trip" />
        </div>
      </Screen>
    );
  }

  if (error || !context) {
    return (
      <Screen maxWidth={560}>
        <Alert tone="error" title="Could not open this trip">
          {error ?? 'It may have been deleted, or you may have been removed from it.'}
        </Alert>
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Link to="/">
            <Button variant="secondary">Back to your trips</Button>
          </Link>
        </div>
      </Screen>
    );
  }

  return (
    <>
      <Screen maxWidth={560} style={{ paddingBottom: BOTTOM_GUTTER }}>
        <Outlet context={context} />
      </Screen>

      <button
        type="button"
        onClick={() => openExpenseSheet()}
        aria-label="Add an expense"
        style={{
          position: 'fixed',
          right: 'max(var(--sp-5), calc(50vw - 260px))',
          bottom: 'calc(84px + env(safe-area-inset-bottom))',
          zIndex: 45,
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          border: 'none',
          background: 'var(--accent)',
          color: '#fff',
          boxShadow: '0 6px 20px rgba(14,122,99,0.34)',
          cursor: 'pointer',
        }}
      >
        <PlusIcon size={26} weight={2} />
      </button>

      <TabBar
        tabs={[
          { to: `/trips/${tripId}`, label: 'Trip', Icon: HomeIcon, end: true },
          { to: `/trips/${tripId}/expenses`, label: 'Expenses', Icon: ReceiptIcon },
          { to: `/trips/${tripId}/settle`, label: 'Settle up', Icon: SwapIcon },
          { to: `/trips/${tripId}/group`, label: 'Group', Icon: UsersIcon },
        ]}
      />

      <ExpenseSheet
        open={sheetOpen}
        trip={context.trip}
        members={context.members}
        rates={context.rates}
        myMemberId={context.myMemberId}
        expense={editing}
        onClose={() => setSheetOpen(false)}
        onSaved={() => {
          setSheetOpen(false);
          reload();
        }}
      />
    </>
  );
}
